/* ═══════════════════════════════════════════════════
   WHAT'S NEXT — curated promo carousel

   The scrolling itself is native (CSS scroll-snap), so touch and trackpad
   swiping work without any JS. This only adds the arrows and dots, and hides
   both when everything already fits on screen — a row of 3 cards on a wide
   monitor shouldn't sprout controls that do nothing.
   ═══════════════════════════════════════════════════ */

(function () {
  /* ── Vimeo thumbnails for auto tip cards ──

     Tips carry no image of their own; the picture that belongs to a tip is a
     frame of its video. Vimeo's oEmbed endpoint hands that over for a plain
     video URL, cross-origin, no key — so the card can build its own artwork.

     Two things keep this cheap: results are cached per video in localStorage
     for a month, and only the handful of auto cards on screen are ever asked
     for. A failure is silent by design — the card keeps its placeholder and
     everything else about it still works. */
  var THUMB_PREFIX = 'filmstudy_vthumb_';
  var THUMB_TTL = 30 * 24 * 60 * 60 * 1000;
  /* Failures are remembered too, briefly. A deleted or private video would
     otherwise be re-requested on every single dashboard view. A day is short
     enough that fixing the video shows up the next day on its own. */
  var THUMB_FAIL_TTL = 24 * 60 * 60 * 1000;

  /* Returns the cached src, the string 'failed', or null for "ask Vimeo". */
  function readThumbCache(url) {
    try {
      var raw = localStorage.getItem(THUMB_PREFIX + url);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry) return null;
      var age = Date.now() - (entry.ts || 0);
      if (entry.failed) return age > THUMB_FAIL_TTL ? null : 'failed';
      if (!entry.src || age > THUMB_TTL) return null;
      return entry.src;
    } catch (err) {
      return null;
    }
  }

  function writeThumbCache(url, src) {
    try {
      var entry = src ? { src: src, ts: Date.now() } : { failed: true, ts: Date.now() };
      localStorage.setItem(THUMB_PREFIX + url, JSON.stringify(entry));
    } catch (err) {
      /* Quota or blocked storage — we just re-fetch next visit. */
    }
  }

  /* oEmbed returns a 295x166 crop regardless of the width asked for. The CDN
     will render any size if you rewrite the suffix, and these cards are far
     bigger than 295px wide. */
  function upsize(src) {
    return String(src || '').replace(/-d_\d+x\d+/, '-d_960x540');
  }

  function paintThumb(card, src) {
    var slot = card.querySelector('[data-wn-thumb]');
    if (!slot || !slot.parentNode) return;
    var img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    /* If the frame 404s, put the placeholder back rather than showing a
       broken-image icon over the title. */
    img.onerror = function () {
      if (img.parentNode) img.parentNode.replaceChild(slot, img);
    };
    slot.parentNode.replaceChild(img, slot);
  }

  function loadThumbs(root) {
    var cards = root.querySelectorAll('[data-wn-vimeo]');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.getAttribute('data-wn-thumb-done') === 'true') return;
      var url = card.getAttribute('data-wn-vimeo');
      if (!url) return;
      card.setAttribute('data-wn-thumb-done', 'true');

      /* VIDEOS MOVED TO LIVID (2026-10-21). Vimeo's oEmbed dies with the
         Vimeo library, and Livid's oEmbed carries no thumbnail, so the
         artwork comes from assets/livid-thumbs.js (video id -> Livid's own
         thumbnail). No entry: the placeholder stands, as before. */
      var m = String(url || '').match(/livid\.com\/(?:oe\/[A-Za-z0-9_-]+\/video|embed)\/([A-Za-z0-9_-]+)|vimeo\.com\/(?:video\/)?(\d+)/);
      var map = window.FilmStudyLividThumbs || {};
      var src = (m && map[m[1] || m[2]]) || '';
      if (src) paintThumb(card, src);
    });
  }

  function initWhatsNext(root) {
    var track = root.querySelector('[data-wn-track]');
    if (!track) return;

    var nav = root.querySelector('[data-wn-nav]');
    var dotsWrap = root.querySelector('[data-wn-dots]');
    var prev = root.querySelector('[data-wn-prev]');
    var next = root.querySelector('[data-wn-next]');
    var cards = track.querySelectorAll('.m-whats-next__card');
    if (!cards.length) return;

    loadThumbs(root);

    /* How far one "page" of the carousel is. Reading it off the live layout
       rather than hardcoding keeps this correct at every breakpoint. */
    function step() {
      if (cards.length < 2) return track.clientWidth;
      var a = cards[0].getBoundingClientRect().left;
      var b = cards[1].getBoundingClientRect().left;
      var gap = Math.round(b - a);
      return gap > 0 ? gap : track.clientWidth;
    }

    function overflowing() {
      return track.scrollWidth - track.clientWidth > 4;
    }

    function pageCount() {
      var per = Math.max(1, Math.round(track.clientWidth / step()));
      return Math.max(1, Math.ceil(cards.length / per));
    }

    /* Which dot to light up. Deliberately based on how far along the scroll
       actually is, not on "which page are we on" — the last page is usually
       partial (4 cards in rows of 3 can only scroll one card's worth), so page
       arithmetic leaves the final dot unreachable. */
    function currentPage() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      var total = pageCount();
      if (maxScroll <= 0 || total < 2) return 0;
      var ratio = Math.min(1, Math.max(0, track.scrollLeft / maxScroll));
      return Math.round(ratio * (total - 1));
    }

    function buildDots() {
      if (!dotsWrap) return;
      var total = pageCount();
      if (!overflowing() || total < 2) {
        dotsWrap.hidden = true;
        dotsWrap.innerHTML = '';
        return;
      }
      dotsWrap.hidden = false;
      dotsWrap.innerHTML = '';
      for (var i = 0; i < total; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'm-whats-next__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.setAttribute('data-wn-dot', String(i));
        dotsWrap.appendChild(dot);
      }
      syncDots();
    }

    function syncDots() {
      if (!dotsWrap || dotsWrap.hidden) return;
      var active = currentPage();
      var dots = dotsWrap.querySelectorAll('[data-wn-dot]');
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === active);
      }
    }

    function syncArrows() {
      if (!nav) return;
      if (!overflowing()) {
        nav.hidden = true;
        return;
      }
      nav.hidden = false;
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
    }

    function sync() {
      syncArrows();
      syncDots();
    }

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollLeft -= step();
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        track.scrollLeft += step();
      });
    }

    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (event) {
        var dot = event.target.closest('[data-wn-dot]');
        if (!dot) return;
        /* Mirror of currentPage(): map the dot back onto the scroll range. */
        var total = pageCount();
        var maxScroll = track.scrollWidth - track.clientWidth;
        var index = parseInt(dot.getAttribute('data-wn-dot'), 10);
        track.scrollLeft = total < 2 ? 0 : (index / (total - 1)) * maxScroll;
      });
    }

    track.addEventListener('scroll', function () {
      window.clearTimeout(root._wnScrollTimer);
      root._wnScrollTimer = window.setTimeout(sync, 60);
    });

    window.addEventListener('resize', function () {
      window.clearTimeout(root._wnResizeTimer);
      root._wnResizeTimer = window.setTimeout(function () {
        buildDots();
        sync();
      }, 120);
    });

    buildDots();
    sync();
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = container.querySelectorAll ? container.querySelectorAll('.m-whats-next') : [];
    Array.prototype.forEach.call(roots, initWhatsNext);
    if (container.classList && container.classList.contains('m-whats-next')) {
      initWhatsNext(container);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme Editor swaps section markup over AJAX without re-running this file. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
